import { HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, Injectable, InjectionToken, effect, inject, untracked } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { OtpAuthService } from './otp-auth.service';
import { TokenStorageService } from './token-storage.service';

export type RefreshOutcome = 'refreshed' | 'rejected' | 'transient';

export const WEB_LOCKS = new InjectionToken<LockManager | null>('WEB_LOCKS', {
  providedIn: 'root',
  factory: () => navigator.locks ?? null,
});

const LOCK_NAME = 'to-refresh';
const REFRESH_LEAD_MS = 60_000;
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class OtpRefreshService {
  private readonly otpAuth = inject(OtpAuthService);
  private readonly tokenStorage = inject(TokenStorageService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly locks = inject(WEB_LOCKS);

  private inFlight: Promise<RefreshOutcome> | null = null;
  private failures = 0;
  private blockedUntil = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private receivedAt: number | null = null;

  constructor() {
    let initial = true;
    effect(() => {
      const token = this.tokenStorage.accessToken();
      untracked(() => {
        this.receivedAt = initial ? null : Date.now();
        initial = false;
        this.schedule(token);
      });
    });
    inject(DestroyRef).onDestroy(() => clearTimeout(this.timer));
  }

  refresh(staleToken: string | null): Promise<RefreshOutcome> {
    if (Date.now() < this.blockedUntil) {
      return Promise.resolve('transient');
    }
    this.inFlight ??= this.exclusive(() => this.refreshUnderLock(staleToken)).finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  async endSession(): Promise<void> {
    await this.inFlight;
    await this.exclusive(async () => {
      this.tokenStorage.clear();
      await firstValueFrom(this.otpAuth.logout(), { defaultValue: undefined }).catch(() => undefined);
    });
  }

  private schedule(token: string | null): void {
    clearTimeout(this.timer);
    const dueAt = this.refreshDueAt(token);
    if (dueAt === null) {
      return;
    }
    const delay = Math.max(dueAt, this.blockedUntil) - Date.now();
    this.timer = setTimeout(
      () => {
        void this.refresh(token).then((outcome) => {
          if (outcome === 'transient') {
            this.schedule(this.tokenStorage.accessToken());
          }
        });
      },
      Math.max(delay, 0),
    );
  }

  private async refreshUnderLock(staleToken: string | null): Promise<RefreshOutcome> {
    const stored = this.tokenStorage.readStoredAccessToken();
    if (stored === null) {
      return 'rejected';
    }
    if (stored !== staleToken && (this.issuedLater(stored, staleToken) || this.isValidBeyondLead(stored))) {
      return 'refreshed';
    }
    try {
      this.tokenStorage.setTokens(await firstValueFrom(this.otpAuth.refresh()));
      this.resetBackoff();
      return 'refreshed';
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        this.resetBackoff();
        return 'rejected';
      }
      this.backOff(error);
      this.connectivity.setUnavailable();
      return 'transient';
    }
  }

  // A token that arrived while running is fresh, so its due time counts from its arrival with the
  // lifetime the server gave it (exp - iat). A browser clock running ahead of the server would
  // otherwise make every new token look due at once and refresh in a loop.
  private refreshDueAt(token: string | null): number | null {
    const expiresAt = this.tokenStorage.accessTokenExpiresAt(token);
    if (expiresAt === null) {
      return null;
    }
    const lifetime = this.lifetime(token, expiresAt);
    const lead = this.lead(lifetime);
    if (this.receivedAt === null) {
      return expiresAt - lead;
    }
    return lifetime === null ? Math.max(expiresAt - lead, this.receivedAt + lead) : this.receivedAt + lifetime - lead;
  }

  // Comparing the server's own iat claims keeps adoption independent of the browser clock.
  private issuedLater(stored: string, staleToken: string | null): boolean {
    const storedIssuedAt = this.tokenStorage.accessTokenIssuedAt(stored);
    const staleIssuedAt = this.tokenStorage.accessTokenIssuedAt(staleToken);
    return storedIssuedAt !== null && staleIssuedAt !== null && storedIssuedAt > staleIssuedAt;
  }

  private isValidBeyondLead(token: string): boolean {
    const expiresAt = this.tokenStorage.accessTokenExpiresAt(token);
    return expiresAt !== null && expiresAt - Date.now() > this.lead(this.lifetime(token, expiresAt));
  }

  private lifetime(token: string | null, expiresAt: number): number | null {
    const issuedAt = this.tokenStorage.accessTokenIssuedAt(token);
    return issuedAt !== null && expiresAt > issuedAt ? expiresAt - issuedAt : null;
  }

  private lead(lifetime: number | null): number {
    return lifetime === null ? REFRESH_LEAD_MS : Math.min(REFRESH_LEAD_MS, lifetime / 2);
  }

  private backOff(error: unknown): void {
    const exponential = BACKOFF_BASE_MS * 2 ** this.failures;
    const retryAfterSeconds = error instanceof HttpErrorResponse ? Number(error.headers.get('Retry-After')) || 0 : 0;
    this.failures += 1;
    this.blockedUntil = Date.now() + Math.min(Math.max(exponential, retryAfterSeconds * 1_000), BACKOFF_MAX_MS);
  }

  private resetBackoff(): void {
    this.failures = 0;
    this.blockedUntil = 0;
  }

  private exclusive<T>(task: () => Promise<T>): Promise<T> {
    return this.locks ? this.locks.request(LOCK_NAME, task) : task();
  }
}
